import { useEffect, useState } from 'react'
import { useRoutes, useLocation } from 'react-router-dom'

import { Main } from './components'

import {
  ApiOverview,
  CharacterDashboard,
  CommunityDashboard,
  GuildDashboard,
  GuildMembers,
  GuildNews,
  GuildOverview,
  GuildTeams,
  Home,
  Onboarding,
  PrivacyPolicy,
  TeamDashboard,
  TermsOfUse,
  UserDashboard,
} from './pages'
import { AppContext, defaultCharacterRef } from './config/context/app'
import LoginFailure from './pages/LoginFailure'
import { usePageInfo } from './config/queries'
import NotFound from './pages/Error/NotFound'
import TeamOverview from './pages/TeamDashboard/subpages/TeamOverview'
import TeamRoster from './pages/TeamDashboard/subpages/TeamRoster'
import TeamApplicants from './pages/TeamDashboard/subpages/TeamApplicants'
import TeamEventOverview from './pages/TeamDashboard/subpages/TeamEventOverview'
import TeamLoot from './pages/TeamDashboard/subpages/TeamLoot'
import TeamSettings from './pages/TeamDashboard/subpages/TeamSettings'
import PageError from './components/PageError'
import { ErrorBoundary } from 'react-error-boundary'
import Header from './pages/Home/components/Header'
import Footer from './pages/Home/components/Footer'
import TeamEvent from './pages/TeamDashboard/subpages/TeamEvent'
import TeamEventInsights from './pages/TeamDashboard/subpages/TeamEventInsights'
import TeamApplicationDetails from './pages/TeamDashboard/subpages/TeamApplicants/components/TeamApplicationDetails'
import {
  GuildApplicationForm,
  TeamApplicationForm,
} from './pages/TeamDashboard/subpages/TeamApplicants/components/ApplicationForm'
import UserLinkedAccounts from './pages/UserDashboard/subpages/UserLinkedAccounts'
import UserCharacters from './pages/UserDashboard/subpages/UserCharacters'
import UserPremiumStatus from './pages/UserDashboard/subpages/UserPremiumStatus'
import UserSettings from './pages/UserDashboard/subpages/UserSettings'
import UserApplications from './pages/UserDashboard/subpages/UserApplications'
import SingleUserApplication from './pages/UserDashboard/subpages/UserApplications/components/SingleUserApplication'
import UserPlans from './pages/UserDashboard/subpages/UserPlans'
import UserPlanEditor from './pages/UserDashboard/subpages/UserPlans/UserPlanEditor'
import UserEvents from './pages/UserDashboard/subpages/UserEvents'
import TeamNews from './pages/TeamDashboard/subpages/TeamNews'
import TeamAudit from './pages/TeamDashboard/subpages/TeamAudit'
import { cn } from '@/lib/utils'
import InactiveDomain from './pages/Error/InactiveDomain'
import { useCustomDomain } from './hooks/useCustomDomain'
import { useHasSidebar } from './hooks/useHasSidebar'
import TeamPlans from './pages/TeamDashboard/subpages/TeamPlans'

const ScrollToTop = () => {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return null
}

const AppContent = () => {
  const { data: session } = usePageInfo()
  const { customDomain } = useCustomDomain()
  const hasSidebar = useHasSidebar()

  const nestedTeamRoutes = [
    {
      index: true,
      element: (
        <GuildDashboard scope='team' teamSection='base'>
          <TeamOverview />
        </GuildDashboard>
      ),
    },
    {
      path: 'audit',
      element: (
        <GuildDashboard scope='team' teamSection='audit'>
          <TeamAudit />
        </GuildDashboard>
      ),
    },
    {
      path: 'news/*',
      element: (
        <GuildDashboard scope='team' teamSection='news'>
          <TeamNews />
        </GuildDashboard>
      ),
    },
    {
      path: 'roster',
      element: (
        <GuildDashboard scope='team' teamSection='roster'>
          <TeamRoster />
        </GuildDashboard>
      ),
    },
    {
      path: 'apply',
      element: (
        <GuildDashboard scope='team' teamSection='none'>
          <TeamApplicationForm editing={false} />
        </GuildDashboard>
      ),
    },
    {
      path: 'apply/:applicationId',
      element: (
        <GuildDashboard scope='team' teamSection='none'>
          <TeamApplicationForm editing={true} />
        </GuildDashboard>
      ),
    },
    {
      path: 'applicants',
      element: (
        <GuildDashboard scope='team' teamSection='applications'>
          <TeamApplicants />
        </GuildDashboard>
      ),
    },
    {
      path: 'applicants/:applicationId',
      element: (
        <GuildDashboard scope='team' teamSection='applications'>
          <TeamApplicationDetails />
        </GuildDashboard>
      ),
    },
    {
      path: 'events',
      element: (
        <GuildDashboard scope='team' teamSection='events'>
          <TeamEventOverview />
        </GuildDashboard>
      ),
    },
    {
      path: 'events/insights',
      element: (
        <GuildDashboard scope='team' teamSection='events'>
          <TeamEventInsights />
        </GuildDashboard>
      ),
    },
    {
      path: 'events/:eventId',
      element: (
        <GuildDashboard scope='team' teamSection='events'>
          <TeamEvent />
        </GuildDashboard>
      ),
    },
    {
      path: 'loot/*',
      element: (
        <GuildDashboard scope='team' teamSection='loot'>
          <TeamLoot />
        </GuildDashboard>
      ),
    },
    {
      path: 'plans/*',
      element: (
        <GuildDashboard scope='team' teamSection='plans'>
          <TeamPlans />
        </GuildDashboard>
      ),
    },
    {
      path: 'settings',
      element: (
        <GuildDashboard scope='team' teamSection='base' teamRequirement='admin'>
          <TeamSettings />
        </GuildDashboard>
      ),
    },
    {
      path: '*',
      element: (
        <GuildDashboard scope='team' teamRequirement={'none'}>
          <NotFound nested={true} />
        </GuildDashboard>
      ),
    },
  ]

  const nestedGuildRoutes = [
    {
      index: true,
      element: (
        <GuildDashboard scope='guild'>
          <GuildOverview />
        </GuildDashboard>
      ),
    },
    {
      path: 'news',
      element: (
        <GuildDashboard scope='guild'>
          <GuildNews />
        </GuildDashboard>
      ),
    },
    {
      path: 'members',
      element: (
        <GuildDashboard scope='guild'>
          <GuildMembers />
        </GuildDashboard>
      ),
    },
    {
      path: 'teams',
      element: (
        <GuildDashboard scope='guild'>
          <GuildTeams />
        </GuildDashboard>
      ),
    },
    {
      path: 'apply',
      element: (
        <GuildDashboard scope='guild'>
          <GuildApplicationForm editing={false} />
        </GuildDashboard>
      ),
    },
    {
      path: 'teams/:team',
      children: nestedTeamRoutes,
    },
    {
      path: '*',
      element: (
        <GuildDashboard scope='guild'>
          <NotFound nested={true} />
        </GuildDashboard>
      ),
    },
  ]

  const routes = useRoutes(
    session?.customDomain?.active === false
      ? [{ path: '*', element: <InactiveDomain /> }]
      : [
          ...[
            session?.customDomain?.team
              ? {
                  path: '/',
                  children: nestedTeamRoutes,
                }
              : session?.customDomain?.guild
              ? {
                  path: '/',
                  children: nestedGuildRoutes,
                }
              : { path: '/', element: <Home /> },
          ],
          { path: '/onboarding', element: <Onboarding /> },
          { path: '/api', element: <ApiOverview /> },
          { path: '/privacy-policy', element: <PrivacyPolicy /> },
          { path: '/terms-of-use', element: <TermsOfUse /> },
          { path: '/auth/failure', element: <LoginFailure /> },
          {
            path: '/profile',
            children: [
              {
                index: true,
                element: (
                  <UserDashboard>
                    <UserLinkedAccounts />
                  </UserDashboard>
                ),
              },
              {
                path: 'characters',
                element: (
                  <UserDashboard>
                    <UserCharacters />
                  </UserDashboard>
                ),
              },
              {
                path: 'premium',
                element: (
                  <UserDashboard>
                    <UserPremiumStatus />
                  </UserDashboard>
                ),
              },
              ...(customDomain
                ? []
                : [
                    {
                      path: 'plans',
                      element: (
                        <UserDashboard>
                          <UserPlans />
                        </UserDashboard>
                      ),
                    },
                    {
                      path: 'plans/:planId',
                      element: (
                        <UserDashboard>
                          <UserPlanEditor />
                        </UserDashboard>
                      ),
                    },
                    {
                      path: 'events',
                      element: (
                        <UserDashboard>
                          <UserEvents />
                        </UserDashboard>
                      ),
                    },
                  ]),
              {
                path: 'applications',
                element: (
                  <UserDashboard>
                    <UserApplications />
                  </UserDashboard>
                ),
              },
              {
                path: 'applications/:applicationId',
                element: (
                  <UserDashboard>
                    <SingleUserApplication />
                  </UserDashboard>
                ),
              },
              {
                path: 'settings',
                element: (
                  <UserDashboard>
                    <UserSettings />
                  </UserDashboard>
                ),
              },
            ],
          },
          {
            path: '/character/:region/:realm/:character',
            element: <CharacterDashboard />,
          },
          ...(session?.customDomain
            ? []
            : [
                {
                  path: '/guild/:region/:realm/:guild',
                  children: nestedGuildRoutes,
                },
                {
                  path: '/community/:community',
                  children: [
                    { index: true, element: <CommunityDashboard /> },
                    {
                      path: 'teams/:team',
                      children: [{ index: true, element: <TeamDashboard /> }],
                    },
                  ],
                },
                {
                  // Personal user team
                  path: '/personal/team',
                  children: [{ index: true, element: <TeamDashboard /> }],
                },
              ]),
          {
            path: '*',
            element: <NotFound />,
          },
        ],
  )

  const renderFullErrorPage = () => {
    return (
      <Main>
        <PageError />
      </Main>
    )
  }

  return (
    <div className={cn('flex', hasSidebar ? '' : 'flex-col')}>
      <Header />

      <div
        className={cn(
          'flex flex-col h-full min-h-[calc(100vh-57px)]',
          hasSidebar ? 'grow min-h-screen' : 'min-h-[calc(100vh-57px)]',
        )}
      >
        <ErrorBoundary
          FallbackComponent={renderFullErrorPage}
          resetKeys={[location.pathname]}
        >
          {routes}
        </ErrorBoundary>
        <ScrollToTop />
        {hasSidebar ? null : <Footer />}
      </div>
    </div>
  )
}

const App = () => {
  const { data: session } = usePageInfo()
  const [customDomainPreview, setCustomDomainPreviewState] = useState(
    session?.customDomain,
  )
  const [currentCharacterRef, setCurrentCharacterRef] = useState(
    defaultCharacterRef(session),
  )

  const setAndStoreCurrentCharacterRef = (
    characterRef?: wowaudit.CharacterReference,
  ) => {
    setCurrentCharacterRef(characterRef)
    if (characterRef)
      localStorage.setItem('currentCharacterId', characterRef.id.toString())
  }

  return (
    <AppContext.Provider
      value={{
        customDomain: customDomainPreview,
        setCustomDomainPreview: setCustomDomainPreviewState,
        currentCharacterRef,
        setCurrentCharacterRef: setAndStoreCurrentCharacterRef,
      }}
    >
      <AppContent />
    </AppContext.Provider>
  )
}

export default App
